import labelsJson from './data/region-tag-labels.json';

/**
 * TEC-482: per-locale display labels + type classification for regionTags facet values. Without
 * this, the search filter shows raw gazetteer place ids (e.g. "658225") for city/admin1/country
 * entries instead of "Helsinki", only continent codes and recruiting-region codes were ever
 * human-readable on their own.
 *
 * Generated by scripts/generate-region-tag-labels.ts from the same checked-in seed data the
 * gazetteer itself is built from (scripts/data/geonames/*.json,
 * scripts/data/recruiting-regions.json), re-run that script by hand if the seed data changes.
 *
 * `type` lets the search UI group facet values (regions / continents / countries / Finland)
 * instead of rendering one flat ~1,200-item list, see FilterBuildingBlocks.tsx's
 * `processedFacetItems` regionTags branch.
 */
export type RegionTagType = 'continent' | 'recruitingRegion' | 'country' | 'admin1' | 'city';

interface RegionTagLabelEntry {
  en: string;
  fi: string;
  sv: string;
  type: RegionTagType;
  /** Country entries only: ISO 3166-1 alpha-2 (see getRegionTagValueForCountryCode). */
  countryCode?: string;
}

const LABELS = labelsJson as Record<string, RegionTagLabelEntry>;

/** ISO 3166-1 alpha-2 -> country placeId, built lazily on first use from the country entries. */
let countryCodeToPlaceId: Map<string, string> | null = null;

/** Falls back to the English label, then the raw value, when unknown or the locale is missing, never throws, never shows nothing. */
export function getRegionTagLabel(value: string, locale: 'en' | 'fi' | 'sv' = 'en'): string {
  const entry = LABELS[value];
  if (!entry) return value;
  return entry[locale] || entry.en || value;
}

export function getRegionTagType(value: string): RegionTagType | undefined {
  return LABELS[value]?.type;
}

/** True when `value` has a real display label (i.e. is a known gazetteer place / region code), false for raw unknown ids. */
export function hasRegionTagLabel(value: string): boolean {
  return Boolean(LABELS[value]);
}

/**
 * Maps an ISO 3166-1 alpha-2 country code (the format `remoteEligibility.ref` uses for
 * `scopeType: 'country'`, and `remoteEligibleCountryCodes` stores) to that country's gazetteer
 * placeId, i.e. its `regionTags` value. Undefined for an unknown code, never throws.
 */
export function getRegionTagValueForCountryCode(countryCode: string): string | undefined {
  if (!countryCodeToPlaceId) {
    countryCodeToPlaceId = new Map<string, string>();
    for (const [placeId, entry] of Object.entries(LABELS)) {
      if (entry.type === 'country' && entry.countryCode) countryCodeToPlaceId.set(entry.countryCode, placeId);
    }
  }
  return countryCodeToPlaceId.get(countryCode.toUpperCase());
}

/**
 * The reverse of {@link getRegionTagValueForCountryCode}: a country placeId's own ISO 3166-1
 * alpha-2 code, e.g. for rendering a flag emoji on the map's country bubbles (see
 * countryCodeToFlagEmoji in components/map/map-marker-dom.ts). Undefined for a non-country value
 * or an unknown placeId, never throws.
 */
export function getCountryCodeForRegionTagValue(value: string): string | undefined {
  return LABELS[value]?.type === 'country' ? LABELS[value]?.countryCode : undefined;
}

/** name (any locale, lowercased) -> city placeId, built lazily on first use. */
let cityNameToPlaceId: Map<string, string> | null = null;
const FINLAND_COUNTRY_SUFFIX_WORDS = new Set(['finland', 'suomi']);

/**
 * TEC-482 Phase 8: reverse lookup from a Finnish city's display name to its gazetteer placeId —
 * the bridge from an incoming legacy display string (a decoded URL slug, an old `location` facet
 * filter value, a `location=` query param) to the placeId keys the migrated data-generation
 * pipelines now use. Accepts a bare name ("Helsinki") or one with the old flat-facet country
 * suffix ("Helsinki, Finland" / "Turku, Suomi"), case-insensitive, matched against the city's
 * en/fi/sv labels. Undefined for anything not a recognised Finnish city name.
 *
 * Safe to key on name alone without a country check because the checked-in gazetteer seed is
 * Finland-only as of this writing (scripts/data/geonames/README.md) — every `type: 'city'` entry
 * in region-tag-labels.json is a Finnish city, so there's no other-country collision risk yet.
 * Revisit (scope this to Finland placeIds explicitly) if the gazetteer is ever seeded
 * internationally.
 */
export function getFinlandCityPlaceIdForName(name: string): string | undefined {
  if (!cityNameToPlaceId) {
    cityNameToPlaceId = new Map<string, string>();
    for (const [placeId, entry] of Object.entries(LABELS)) {
      if (entry.type !== 'city') continue;
      for (const label of [entry.en, entry.fi, entry.sv]) {
        if (label) cityNameToPlaceId.set(label.toLowerCase(), placeId);
      }
    }
  }
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  const commaIdx = trimmed.lastIndexOf(',');
  if (commaIdx <= 0) return cityNameToPlaceId.get(trimmed.toLowerCase());
  const town = trimmed.slice(0, commaIdx).trim();
  const suffix = trimmed.slice(commaIdx + 1).trim().toLowerCase();
  if (!town || !FINLAND_COUNTRY_SUFFIX_WORDS.has(suffix)) return undefined;
  return cityNameToPlaceId.get(town.toLowerCase());
}
